import React, { useState } from 'react';
import { supabase } from '../../lib/supabase'; // adjust path if needed
import { getCurrentUser } from '../../lib/auth';

const categories = [
  { id: 'bug',      icon: '🐛', label: 'Something is broken',     desc: 'Report a bug or error' },
  { id: 'feedback', icon: '💡', label: 'Feedback or suggestion',  desc: 'Tell us what you think' },
  { id: 'question', icon: '❓', label: 'I have a question',        desc: 'We will get back to you' },
  { id: 'other',    icon: '💬', label: 'Something else',           desc: 'Anything on your mind' },
];

export default function FeedbackWidget({ portal = 'reader' }) {
  const [isOpen,    setIsOpen]    = useState(false);
  const [category,  setCategory]  = useState(null);
  const [message,   setMessage]   = useState('');
  const [email,     setEmail]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const accentColor = portal === 'reader' ? '#B85C38' : '#2D4A3E';

  const reset = () => {
    setCategory(null);
    setMessage('');
    setEmail('');
    setSubmitted(false);
    setError(null);
  };

  async function handleSubmit() {
    if (!message.trim()) return;
    setLoading(true);
    setError(null);

    // Get current user if logged in
    const user = await getCurrentUser();

    const { error: insertError } = await supabase.from('feedback').insert({
      category,
      message: message.trim(),
      email:   email.trim() || null,
      portal,
      user_id: user?.id || null,
      status:  'open',
    });

    setLoading(false);

    if (insertError) {
      setError('Something went wrong. Please try again.');
    } else {
      setSubmitted(true);
    }
  }

  return (
    <>
      {/* Floating trigger button */}
      {!isOpen && (
        <button
          onClick={() => { setIsOpen(true); reset(); }}
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
            width: 56, height: 56, borderRadius: 'var(--radius-circle)',
            background: accentColor, border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0,0,0,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.22)'; }}
          title="Send feedback"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z" fill="#fff"/>
          </svg>
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9000,
          width: 360, background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)',
          boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
          border: '1px solid var(--brand-sand)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: 480,
          fontFamily: 'var(--font-body)',
        }}>

          {/* Header */}
          <div style={{ background: accentColor, padding: '16px 20px', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.05, background: 'repeating-linear-gradient(45deg, var(--brand-white) 0px, var(--brand-white) 1px, transparent 1px, transparent 6px)' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
                  <div style={{ width: 100, height: 30, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                   <img src='/logo2.png' alt='Reanlo' style={{ width: "100%", height: 50, objectFit: 'cover' }} />
                  </div>
                </div>
                <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--brand-white)', margin: 'var(--space-none)' }}>
                  {submitted ? 'Thank you!' : !category ? 'How can we help?' : 'Send us a message'}
                </h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 'var(--radius-circle)', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--brand-white)', fontSize: 'var(--font-size-md)' }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>

            {/* Step 1 — category */}
            {!category && !submitted && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: '12px 14px', borderRadius: 'var(--radius-lg)', cursor: 'pointer', border: '1px solid var(--brand-sand)', background: 'var(--brand-white)', textAlign: 'left', transition: 'background 0.1s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#faf8f5'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >
                    <span style={{ fontSize: 'var(--font-size-xxl)' }}>{cat.icon}</span>
                    <div>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', margin: 'var(--space-none)' }}>{cat.label}</p>
                      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>{cat.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2 — message */}
            {category && !submitted && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
                  <button onClick={() => setCategory(null)} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-none)' }}>←</button>
                  <span style={{ fontSize: 'var(--font-size-md)' }}>{categories.find(c => c.id === category).icon}</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)' }}>{categories.find(c => c.id === category).label}</span>
                </div>

                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={
                    category === 'bug'      ? 'What happened? What did you expect?' :
                    category === 'feedback' ? 'What would you change or improve?' :
                    category === 'question' ? 'What do you need help with?' :
                    'What\'s on your mind?'
                  }
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', outline: 'none', resize: 'none', minHeight: 100, boxSizing: 'border-box', fontFamily: 'inherit', color: 'var(--neutral-800)', lineHeight: 1.5, marginBottom: 'var(--space-10)' }}
                />

                {category === 'bug' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '8px 12px', background: 'var(--feedback-warning-bg)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-10)', border: '1px solid var(--feedback-warning-border)' }}>
                    <span style={{ fontSize: 'var(--font-size-md)' }}>📸</span>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning-ink)', margin: 'var(--space-none)' }}>Describe the page you were on and what you expected to happen.</p>
                  </div>
                )}

                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 600, color: 'var(--neutral-800)', margin: '0 0 4px' }}>
                    Your email:
                  </p>
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-base)', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                {error && (
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-error)', marginBottom: 'var(--space-sm)' }}>{error}</p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || loading}
                  style={{
                    width: '100%', padding: 'var(--space-md)',
                    background: message.trim() && !loading ? accentColor : 'var(--brand-sand)',
                    color: message.trim() && !loading ? 'var(--brand-white)' : 'var(--neutral-400)',
                    border: 'none', borderRadius: 'var(--radius-lg)',
                    fontSize: 'var(--font-size-md)', fontWeight: 700,
                    cursor: message.trim() && !loading ? 'pointer' : 'default',
                    fontFamily: 'inherit',
                  }}
                >
                  {loading ? 'Sending…' : 'Send'}
                </button>
              </div>
            )}

            {/* Step 3 — success */}
            {submitted && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-circle)', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', border: '2px solid #a7f3d0' }}>
                  <span style={{ fontSize: 22 }}>✓</span>
                </div>
                <p style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px' }}>We got your message.</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '0 0 16px', lineHeight: 1.5 }}>
                  {email ? `We'll get back to you at ${email}.` : 'Thanks for helping us make Reanlo better.'}
                </p>
                <button
                  onClick={() => { reset(); setIsOpen(false); }}
                  style={{ padding: '10px 24px', background: accentColor, color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}